import { View, Text } from 'react-native'
import { Pressable } from '@/components/ui/Pressable'
import { useTranslation } from 'react-i18next'
import { Car, Check, Moon, Sun } from '@/components/ui/icons'
import { useTheme } from '../../../lib/hooks/use-theme'
import { useSemanticColors } from '../../../lib/theme/tokens'
import { COLORS, FONTS, RADIUS, SPACING } from '../../../lib/brand'
import { ScreenHeader } from '../../../components/ui/ScreenHeader'
import { ScreenContainer } from '../../../components/ui/ScreenContainer'
import { Card } from '../../../components/ui/Card'
import type { ThemeMode } from '../../../lib/theme/theme.types'
import { selection } from '@/lib/utils/haptics'

// The two option cards are the same control twice, so they must be the same
// size. Their descriptions are not the same length — "Interfaz clara para uso
// de día." wraps and drops "día." onto a second line while "Menos brillo para
// la noche." fits one — which made Claro ~30pt taller than Oscuro. Reserving
// exactly two lines' worth of height in both cards removes the dependency on
// how a given locale happens to break.
//
// The reserve belongs to the text COLUMN, not to the description itself.
// Reserving it on the description left the spare line inside the description's
// own box, so in the one-line card the title and the description sat ~10pt
// above the icon and the radio — which are centred by the row — and the two
// halves of the card visibly did not line up. With the reserve on the column
// and the content centred inside it, both cards keep the same height and every
// element in the row shares one centre line.
const OPTION_TITLE_LINE_HEIGHT = 22
const OPTION_DESC_LINE_HEIGHT = 19
const OPTION_DESC_LINES = 2
const OPTION_TEXT_MIN_HEIGHT =
  OPTION_TITLE_LINE_HEIGHT + SPACING.xs + OPTION_DESC_LINE_HEIGHT * OPTION_DESC_LINES

const OPTIONS: { mode: ThemeMode; icon: typeof Sun; titleKey: string; descKey: string }[] = [
  {
    mode: 'light',
    icon: Sun,
    titleKey: 'settings.appearance.light',
    descKey: 'settings.appearance.light_desc',
  },
  {
    mode: 'dark',
    icon: Moon,
    titleKey: 'settings.appearance.dark',
    descKey: 'settings.appearance.dark_desc',
  },
]

export default function AppearanceScreen() {
  const { t } = useTranslation()
  const { mode, setMode, isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const handleSelect = async (selected: ThemeMode) => {
    if (selected === mode) return
    await selection()
    setMode(selected)
  }

  return (
    <ScreenContainer variant="reading">
      <View style={{ paddingHorizontal: SPACING.lg }}>
        <ScreenHeader
          title={t('settings.appearance.title')}
          style={{ paddingHorizontal: 0, paddingTop: SPACING.sm, paddingBottom: 0 }}
        />
        <Text
          style={{
            marginTop: SPACING.md,
            fontFamily: FONTS.body,
            fontSize: 15,
            lineHeight: 23,
            color: colors.textSecondary,
          }}
        >
          {t('settings.appearance.subtitle')}
        </Text>
      </View>

      <View
        style={{ paddingHorizontal: SPACING.lg, paddingTop: SPACING.xl, gap: SPACING.md }}
        accessibilityRole="radiogroup"
      >
        {OPTIONS.map(({ mode: optionMode, icon: Icon, titleKey, descKey }) => {
          const isSelected = mode === optionMode
          return (
            <Pressable
              key={optionMode}
              onPress={() => handleSelect(optionMode)}
              accessibilityRole="radio"
              accessibilityState={{ checked: isSelected }}
              accessibilityLabel={t(titleKey)}
              testID={`appearance.${optionMode}`}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                borderRadius: RADIUS.xl + 2,
                borderWidth: 1.5,
                borderColor: isSelected ? COLORS.coral.DEFAULT : colors.border,
                backgroundColor: isSelected
                  ? isDark
                    ? 'rgba(239,78,74,0.16)'
                    : 'rgba(239,78,74,0.08)'
                  : colors.surface,
                padding: SPACING.lg,
                opacity: pressed ? 0.76 : 1,
              })}
            >
              <View
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: RADIUS.xl,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isSelected
                    ? COLORS.coral.DEFAULT
                    : isDark
                      ? 'rgba(255,255,255,0.08)'
                      : colors.surfaceSubtle,
                }}
              >
                <Icon size={22} color={isSelected ? '#FFFFFF' : colors.textSecondary} />
              </View>

              <View
                style={{
                  flex: 1,
                  marginLeft: SPACING.lg,
                  minHeight: OPTION_TEXT_MIN_HEIGHT,
                  justifyContent: 'center',
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
                  <Text
                    style={{
                      flexShrink: 1,
                      fontFamily: FONTS.bodyBold,
                      fontSize: 16,
                      lineHeight: OPTION_TITLE_LINE_HEIGHT,
                      color: colors.textPrimary,
                    }}
                  >
                    {t(titleKey)}
                  </Text>
                  {isSelected && (
                    <Text
                      style={{
                        fontFamily: FONTS.body,
                        fontSize: 12,
                        lineHeight: 16,
                        // Neutral, not coral: this pill is a coral tint sitting
                        // inside a card that is already a coral tint, and the
                        // two stack. Coral text on top measured 1.27:1 in dark
                        // mode. The pill and the check already say "selected";
                        // the word only has to be readable.
                        // Solid fill, not another alpha tint. This pill sits
                        // inside a card that is itself a coral tint, the two
                        // stack, and modelling the composite kept disagreeing
                        // with the measurement — 3.58:1 in dark. A known fill
                        // with white on it is 5.50:1 in both themes by
                        // construction.
                        color: '#FFFFFF',
                        backgroundColor: COLORS.coral.dark,
                        borderRadius: 999,
                        paddingHorizontal: SPACING.sm,
                        paddingVertical: 2,
                      }}
                    >
                      {t('settings.appearance.selected')}
                    </Text>
                  )}
                </View>
                <Text
                  numberOfLines={OPTION_DESC_LINES}
                  style={{
                    marginTop: SPACING.xs,
                    fontFamily: FONTS.body,
                    fontSize: 13,
                    lineHeight: OPTION_DESC_LINE_HEIGHT,
                    color: colors.textSecondary,
                  }}
                >
                  {t(descKey)}
                </Text>
              </View>

              <View
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 13,
                  borderWidth: 2,
                  // Not `border`: that token is a divider between two surfaces
                  // (1.26:1 on the card in light, 1.42:1 in dark) and this ring
                  // is the control itself — the unselected option was reading
                  // as a faint smudge rather than a radio button. `textMuted`
                  // is the lightest token that clears the 3:1 a non-text
                  // control needs: 5.2:1 light, 5.2:1 dark.
                  borderColor: isSelected ? COLORS.coral.DEFAULT : colors.textMuted,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: SPACING.sm,
                  backgroundColor: isSelected ? COLORS.coral.DEFAULT : 'transparent',
                }}
              >
                {isSelected && <Check size={14} color="#FFFFFF" strokeWidth={3} />}
              </View>
            </Pressable>
          )
        })}
      </View>

      {/* Preview card */}
      <View style={{ paddingHorizontal: SPACING.lg, paddingTop: SPACING['2xl'] }}>
        <Text
          style={{
            marginBottom: SPACING.sm,
            fontFamily: FONTS.bodyBold,
            fontSize: 14,
            color: colors.textSecondary,
          }}
        >
          {t('settings.appearance.preview_card_heading')}
        </Text>
        {/* Plain `surface`, like the option cards above. The dark-only
            `rgba(255,255,255,0.06)` override composited to a neutral grey
            (#1F1C29) while the option cards sat at the palette's blue-violet
            #242140, so two sibling cards on one screen read as two different
            design systems. */}
        <Card padding="lg" radius="xl" elevation="sm">
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.lg }}>
            {/* A centred car glyph, not a tinted tile with a small pale bar
                floating in the bottom-left: that shape is the loading
                skeleton used everywhere else in the app, so the one card
                whose job is to show what a real listing looks like read as
                an image that never resolved. */}
            <View
              style={{
                width: 72,
                height: 72,
                borderRadius: RADIUS.lg,
                backgroundColor: previewThumbnailTint(isDark),
                borderWidth: 1,
                borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(45,42,94,0.08)',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <Car size={34} color={COLORS.coral.DEFAULT} strokeWidth={1.6} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 16, color: colors.textPrimary }}>
                {t('settings.appearance.preview_title')}
              </Text>
              <Text
                style={{
                  marginTop: 4,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  color: colors.textSecondary,
                }}
              >
                {t('settings.appearance.preview_meta')}
              </Text>
              <Text
                style={{
                  marginTop: SPACING.sm,
                  fontFamily: FONTS.bodyBold,
                  fontSize: 15,
                  color: colors.textAccent,
                }}
              >
                {t('settings.appearance.preview_price')}
              </Text>
            </View>
          </View>
        </Card>
      </View>
    </ScreenContainer>
  )
}

function previewThumbnailTint(isDark: boolean) {
  return isDark ? 'rgba(239,78,74,0.18)' : 'rgba(239,78,74,0.12)'
}
